/* eslint-disable no-await-in-loop */

import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { asyncFetch } from '../../services/UseFetch';
import { addPlmPrefix, usePlmId } from '../../utilities/PgmUtilities';
import { useUserGroups } from '../../stores/SessionStore';

const STATUS = {
    SUCCESS: 0,
    BUSY: 1,
    IN_PROGRESS: 2,
    CANCELLED: 3,
    TIMEOUT: 4,
    INTERNAL_ERROR: 5,
};

const getStatusString = (status) => {
    switch (status) {
        case STATUS.SUCCESS:
            return 'SUCCESS';
        case STATUS.BUSY:
            return 'BUSY';
        case STATUS.IN_PROGRESS:
            return 'IN_PROGRESS';
        case STATUS.CANCELLED:
            return 'CANCELLED';
        case STATUS.TIMEOUT:
            return 'TIMEOUT';
        case STATUS.INTERNAL_ERROR:
            return 'INTERNAL_ERROR';
        default:
            return `UNKNOWN STATUS ${status}`;
    }
};

export function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function asyncFetchProblems(timeRange, entityType, entityUuid, numberToFetch, cancelRef, plmId, userGroups) {
    const requestId = uuidv4();
    const params = {
        start: timeRange.start,
        end: timeRange.end,
        requestId,
        rowLimit: numberToFetch,
        rowStart: 0,
        [entityType]: entityUuid,
        type: 'all',
        userGroups,
    };

    let status;
    let result;
    do {
        result = await asyncFetch(addPlmPrefix('/query/problems', plmId), params);
        if (cancelRef.current) {
            return undefined;
        }
        status = result.status;
        if (status === STATUS.IN_PROGRESS) {
            await sleep(1000); // Pause before the next request
        }
    } while (status === STATUS.IN_PROGRESS);

    if (status !== STATUS.SUCCESS) {
        throw new Error(`Received Status Code ${getStatusString(status)}`);
    }
    return result;
}

export function useFetchProblems(timeRange, entityType, entityUuid, numberToFetch = 10) {
    const plmId = usePlmId();
    const [data, setData] = useState();
    const cancelRef = useRef(false);
    const [userGroups] = useUserGroups();
    useEffect(() => {
        let result;
        try {
            (async function w() {
                result = await asyncFetchProblems(
                    timeRange,
                    entityType,
                    entityUuid,
                    numberToFetch,
                    cancelRef,
                    plmId,
                    userGroups
                );
                if (result) {
                    setData({ result });
                }
            })();
        } catch (e) {
            setData({ error: e });
        }
    }, [timeRange, entityType, entityUuid, numberToFetch, plmId, userGroups]);

    useEffect(() => () => (cancelRef.current = true), [cancelRef]);
    return data;
}
